import { apolloClientPrivate } from '../../../graphql/apolloClient'

import getAWSSignatureV4Form from '../../../utils/getAWSSignatureV4Form'
import { axiosInstance } from '../../../api/http-api'
import { notification } from '../../common/notifications/Notifications'
import { addTimestampToFileName } from './fileUploadCommon'

import {
  TopicElementType,
  UploadCredentials
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { LooopS3BucketEnum } from 'types/UploadCredentials'
import {
  TopicPatchFileDetailsDocument,
  TopicPatchFileDetailsMutation,
  TopicPatchFileDetailsMutationVariables
} from './operations/topicPatchFileDetails.codegen'
import {
  TopicScanFileQuery,
  TopicScanFileQueryVariables,
  TopicScanFileDocument
} from './operations/topicScanFile.codegen'
import {
  UploadCredentialsQuery,
  UploadCredentialsQueryVariables,
  UploadCredentialsDocument
} from './operations/uploadCredentials.codegen'

const getS3Bucket = (type: TopicElementType) => {
  switch (type) {
    case TopicElementType.DOCUMENT:
      return 'topic-documents'
    case TopicElementType.MEDIA:
      return 'topic-videos'
    case TopicElementType.QUIZ_MULTI:
    case TopicElementType.QUIZ_OPEN:
    case TopicElementType.QUIZ_SINGLE:
    case TopicElementType.TEXT:
      return 'topic-images'

    default:
      return null
  }
}

export const fileUploadInTopics = async ({
  topicId,
  elementType,
  elementId,
  blob,
  fileName,
  documentType,
  on
}: {
  topicId: number
  elementType: TopicElementType
  elementId: number
  fileName: string
  blob: Blob
  documentType: 'image' | 'file'
  on?: {
    uploadStart?: (
      data: { __typename?: 'UploadCredentials' } & Pick<
        UploadCredentials,
        'uploadUrl' | 'params'
      >
    ) => void
    uploadProgress?: (number) => void
    uploadComplete?: (number) => void
  }
}) => {
  const newFileName = addTimestampToFileName(fileName)
  let documentFileSize = 0

  const { data } = await apolloClientPrivate.query<
    UploadCredentialsQuery,
    UploadCredentialsQueryVariables
  >({
    variables: {
      filename: newFileName,
      S3path: `${getS3Bucket(elementType)}/topic-${topicId}`,
      bucket: LooopS3BucketEnum.PRIVATE
    },
    query: UploadCredentialsDocument
  })

  if (!data?.awsS3UploadCredentials) {
    notification.warning('Unable to upload')
    return null
  }

  const options = {
    headers: {
      'Content-Type': blob.type
    },
    withCredentials: false,
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      )
      on && on.uploadProgress && on.uploadProgress(percentCompleted)

      if (percentCompleted === 100) {
        documentFileSize = Math.round(progressEvent.total / Math.pow(1024, 1))
        on && on.uploadComplete && on.uploadComplete(documentFileSize)
      }
    }
  }

  const documentFilename = data.awsS3UploadCredentials.params.key
  const documentFileType = blob.type

  on && on.uploadStart && on.uploadStart(data.awsS3UploadCredentials)

  const fd = getAWSSignatureV4Form(
    data.awsS3UploadCredentials.params,
    blob as File
  )

  // TODO use fetch
  await axiosInstance.post(
    data.awsS3UploadCredentials.uploadUrl as string,
    fd,
    options
  )

  if (documentType === 'file') {
    const response = await apolloClientPrivate.mutate<
      TopicPatchFileDetailsMutation,
      TopicPatchFileDetailsMutationVariables
    >({
      variables: {
        topicId,
        elementId,
        input: {
          fileUrl: documentFilename,
          mimeType: documentFileType,
          fileSize: documentFileSize
        }
      },
      mutation: TopicPatchFileDetailsDocument
    })

    const { data } = response
    if (!data?.topic) {
      return null
    }
    const { topic } = data

    if (topic?.__typename === 'TopicModelAsEditor' && topic.draftVersion) {
      const cdnUrl = topic?.draftVersion.element?.patchFileDetails?.data?.cdnUrl // TODO fix
      return cdnUrl?.split('?')[0]
    }
    return null
  } else {
    const { data } = await apolloClientPrivate.query<
      TopicScanFileQuery,
      TopicScanFileQueryVariables
    >({
      variables: {
        topicId,
        elementId,
        fileUrl: documentFilename
      },
      query: TopicScanFileDocument
    })
    const { topic } = data

    if (topic?.__typename === 'TopicModelAsEditor' && topic.draftVersion) {
      return topic?.draftVersion?.element?.scanForViruses
    }
    return null
  }
}
